<template>
    <v-dialog v-model="modal" width="800px" max-width="100%">
        <v-card v-if="info" width="800px" max-width="100%" class="pa-md-3">
            <v-card-text class="text-black">
                <h2 class="pt-5 pt-md-2">
                    {{ info.name }}
                </h2>
                <div v-if="offer.info.description" class="text-subtitle-2">
                    {{ offer.info.description }}
                </div>
                <v-tabs v-model="tab" hide-slider>
                    <v-tab>
                        {{ $t('description') }}
                    </v-tab>
                    <v-tab>
                        {{ $t('arrival_departure') }}
                    </v-tab>
                </v-tabs>
                <v-window v-model="tab">
                    <v-window-item>
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-carousel height="170px" hide-delimiters :show-arrows="images.length > 1">
                                    <v-carousel-item v-for="(image, imageIndex) in images" :key="imageIndex">
                                        <v-img :src="$filters.imageUrl(image.url, 300)" cover height="170px" />
                                    </v-carousel-item>
                                </v-carousel>
                            </v-col>
                            <v-col cols="12" md="8">
                                <template v-if="showShortDescription(info)">
                                    <strong> {{ $t('description_type.short') }} </strong><br />
                                    <div v-html="info.shortDescription" />
                                </template>
                                <template v-if="info.fullDescription">
                                    <strong> {{ $t('description_type.general') }} </strong><br />
                                    <div v-html="info.fullDescription" />
                                </template>
                                <template v-if="info.descriptions && info.descriptions.length">
                                    <strong> {{ $t('description') }} </strong><br />
                                    <div v-html="htmlDescriptions" />
                                </template>
                                <template v-if="info && info.comments && info.comments.length">
                                    <div class="font-weight-bold pt-4 pb-2">
                                        {{ $t('comments') }}
                                    </div>
                                    <div v-for="(comment, commentIndex) in info.comments" :key="commentIndex">
                                        {{ comment }}
                                    </div>
                                </template>
                            </v-col>
                        </v-row>
                    </v-window-item>
                    <v-window-item>
                        <div>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <strong>{{ $t('transfers.departure_location') }} </strong><br />
                                    <div class="mb-1">
                                        <v-icon start>
                                            {{ `mdi-${departurePoint.type}` }}
                                        </v-icon>
                                        {{ departurePoint.name }}, {{ departurePoint.parentName }}
                                    </div>
                                    <v-autocomplete
                                        v-if="info.allowedHotelsDeparture"
                                        variant="outlined"
                                        :label="$t('transfers.possible_hotels')"
                                        hide-details
                                        density="compact"
                                        single-line
                                        :items="info.allowedHotelsDeparture"
                                        item-title="hotelName"
                                        :item-value="item => item"
                                        @update:model-value="departurePoint = $event"
                                    />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <strong>{{ $t('transfers.arrival_location') }} </strong><br />
                                    <div class="mb-1">
                                        <v-icon start>
                                            {{ `mdi-${arrivalPoint.type}` }}
                                        </v-icon>
                                        {{ arrivalPoint.name }}, {{ arrivalPoint.parentName }}
                                    </div>
                                    <v-autocomplete
                                        v-if="info.allowedHotelsArrival"
                                        variant="outlined"
                                        :label="$t('transfers.possible_hotels')"
                                        hide-details
                                        density="compact"
                                        single-line
                                        :items="info.allowedHotelsArrival"
                                        item-title="hotelName"
                                        :item-value="item => item"
                                        @update:model-value="arrivalPoint = $event"
                                    />
                                </v-col>
                            </v-row>
                        </div>
                        <div style="position: relative">
                            <client-only>
                                <transfer-map
                                    style="max-height: 300px; position: unset"
                                    :points="mapPoints"
                                    :parent="true"
                                />
                            </client-only>
                        </div>
                    </v-window-item>
                </v-window>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import TransferMap from '@/components/transfers/search/TransferMap.vue'
    import {EventBus, SHOW_TRANSFER_INFO_MODAL} from '@/utils/event-bus'

    //TODO Need refactoring with other products
    @Component({
        components: {
            TransferMap,
        },
    })
    export class TransferInfoModal extends Vue {
        info = null
        offer = null
        conditionsInfo = {}
        modal = false
        tab = null

        mounted() {
            EventBus.$on(SHOW_TRANSFER_INFO_MODAL, this.showModal)
        }

        beforeUnmount() {
            EventBus.$off(SHOW_TRANSFER_INFO_MODAL, this.showModal)
        }

        showModal({offer, info}) {
            this.offer = offer
            this.info = info
            this.modal = true
        }

        showShortDescription(info) {
            return (
                info.shortDescription &&
                (!info.fullDescription || info.fullDescription.indexOf(info.shortDescription) === -1)
            )
        }

        get images() {
            if (!this.info.images) return []
            if (this.info.images.length && this.info.images[0].linkedEntityName) {
                const images = this.info.images.filter(image => image.linkedEntityName === this.offer.info.description)
                if (images.length) {
                    return images
                }
            }
            return this.info.images
        }

        get mapPoints() {
            return [transfersRuntimeStore.departurePoint, transfersRuntimeStore.arrivalPoint]
        }

        get arrivalPoint() {
            return transfersRuntimeStore.arrivalPoint
        }

        //TODO ???
        set arrivalPoint(point) {}

        get departurePoint() {
            return transfersRuntimeStore.departurePoint
        }

        //TODO ???
        set departurePoint(point) {}

        get htmlDescriptions() {
            if (!this.info?.descriptions?.length) return

            const firstDescription = this.info.descriptions[0]
            if (typeof firstDescription === 'string' || firstDescription instanceof String) {
                return this.info.descriptions.map(item => `<div>${item}</div>`).join('')
            }

            const descriptionsGroupedByTitle = new Map()
            this.info.descriptions.forEach(({title, description}) => {
                const listByTitle = descriptionsGroupedByTitle.get(title)

                if (listByTitle) {
                    listByTitle.push(description)
                } else {
                    descriptionsGroupedByTitle.set(title, [description])
                }
            })

            let fullHTML = ''
            descriptionsGroupedByTitle.forEach((descriptions, title) => {
                let result = ''

                if (title) {
                    result += `<i><strong>${title}</strong></i><br>`
                }

                result += descriptions.join('<br>')

                fullHTML += `<div>${result}</div>`
            })

            return fullHTML
        }
    }

    export default TransferInfoModal
</script>

<style scoped>
    :deep(.v-slide-group__prev.v-slide-group__prev--disabled) {
        display: none !important;
    }
</style>
